/**
 * ServiceUnavailablePage - shown instead of the login page when the backend is
 * unreachable (502/503/504, network failure), typically during a deploy.
 *
 * Deliberately blank: rendering the login form here would offer a password box
 * that cannot work and, for SSO tenants, isn't even the right auth method.
 * The user gets a toast, and the page retries in the background so the app
 * recovers on its own once the backend is back.
 */

import { useEffect, useRef } from 'react'
import { useToast } from '../../components/Toast'
import { useAuth } from '../../contexts/AuthContext'
import { useAuthConfig } from '../../contexts/AuthConfigContext'
import { SERVICE_UNAVAILABLE_MESSAGE } from '../../utils/serviceAvailability'

/** How often to re-check whether the backend is back. */
const RETRY_INTERVAL_MS = 15000

/** Re-show the toast at most this often, so a long outage isn't a toast storm. */
const TOAST_REPEAT_MS = 60000

const ServiceUnavailablePage = () => {
  const toast = useToast()
  const { refreshUser } = useAuth()
  const { refresh: refreshAuthConfig } = useAuthConfig()

  const lastToastAtRef = useRef<number>(0)

  // Toast on mount, then at most once a minute while the outage lasts.
  useEffect(() => {
    const showToast = () => {
      const now = Date.now()
      if (now - lastToastAtRef.current < TOAST_REPEAT_MS) return
      lastToastAtRef.current = now
      toast.error(SERVICE_UNAVAILABLE_MESSAGE, TOAST_REPEAT_MS)
    }

    showToast()
    const toastTimer = setInterval(showToast, TOAST_REPEAT_MS)
    return () => clearInterval(toastTimer)
  }, [toast])

  // Poll in the background; both calls clear their unavailable flags on success,
  // which unmounts this page and restores the normal app/login view.
  useEffect(() => {
    const retry = () => {
      void refreshAuthConfig()
      void refreshUser()
    }

    const retryTimer = setInterval(retry, RETRY_INTERVAL_MS)
    return () => clearInterval(retryTimer)
  }, [refreshAuthConfig, refreshUser])

  return (
    <div
      data-testid="service-unavailable-page"
      aria-hidden="true"
      style={{ minHeight: '100vh', background: 'var(--bg)' }}
    />
  )
}

export default ServiceUnavailablePage
